import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, booleanAttribute, computed, input, output, signal } from '@angular/core';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';

/** Situação cadastral oferecida no modal. */
export interface OpcaoSituacaoModal<T extends string | number> {
  value: T;
  label: string;
}

/**
 * Modal de "Adicionar situação cadastral" sobre o `vivest-modal`, no mesmo formato do
 * "Adicionar documentos" do resgate: seleção múltipla, e as situações que já estão na tela
 * aparecem desabilitadas. Esc é tratado pelo próprio modal (`dismissible`).
 * As opções vêm de quem abre o modal: o catálogo da API no extrato (ids) e a lista fixa na aba
 * Benefícios.
 * No modo "editar" (o lápis da situação cadastral do instituto, no extrato) as situações já
 * incluídas vêm marcadas e podem ser desmarcadas; o modal devolve a lista inteira.
 */
@Component({
  selector: 'app-situacoes-cadastrais-modal',
  standalone: true,
  imports: [VivestModalAreaVisivelDirective],
  // Web components do design system Vivest (modal, checkbox, button).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './situacoes-cadastrais-modal.component.html',
  styleUrl: './situacoes-cadastrais-modal.component.scss',
})
export class SituacoesCadastraisModalComponent<T extends string | number> implements OnInit {
  /** Situações na ordem da tela. */
  readonly opcoes = input.required<readonly OpcaoSituacaoModal<T>[]>();
  /** Situações que já estão marcadas na tela. */
  readonly jaIncluidas = input<readonly T[]>([]);
  /** Texto de apoio: o que a situação escolhida libera (o modal também é usado na aba Benefícios). */
  readonly descricao = input('O participante gera o extrato de desligamento se estiver em uma das situações escolhidas.');
  readonly modo = input<'adicionar' | 'editar'>('adicionar');
  /**
   * Também no "adicionar", as já incluídas vêm marcadas e podem ser desmarcadas, e o modal devolve
   * a lista inteira (extrato). Sem ele, no "adicionar" elas ficam travadas (aba Benefícios).
   */
  readonly editavel = input(false, { transform: booleanAttribute });

  readonly adicionar = output<T[]>();
  readonly fechar = output<void>();

  readonly selecionadas = signal<ReadonlySet<T>>(new Set());
  readonly totalSelecionado = computed(() => this.selecionadas().size);
  readonly editando = computed(() => this.modo() === 'editar');
  /** As já incluídas podem ser desmarcadas e o modal devolve a lista inteira. */
  readonly devolveListaInteira = computed(() => this.editando() || this.editavel());

  ngOnInit(): void {
    if (this.devolveListaInteira()) {
      this.selecionadas.set(new Set(this.jaIncluidas()));
    }
  }

  /** Só no "adicionar" sem `editavel`: a situação já incluída aparece marcada e desabilitada. */
  jaIncluida(situacao: T): boolean {
    return !this.devolveListaInteira() && this.jaIncluidas().includes(situacao);
  }

  alternar(situacao: T): void {
    const proximas = new Set(this.selecionadas());
    if (!proximas.delete(situacao)) {
      proximas.add(situacao);
    }
    this.selecionadas.set(proximas);
  }

  confirmar(): void {
    // Na ordem da lista, não na ordem em que foram marcadas.
    this.adicionar.emit(this.opcoes().map((opcao) => opcao.value).filter((situacao) => this.selecionadas().has(situacao)));
  }
}
